/**
 * Sysadmin Runtime rail — TanStack Query hooks for PR-D (Phase 5).
 *
 * Backs the 4 v1 sub-tabs:
 *   - /sysadmin/runtime/hardware  → useSysadminRuntimeHardware
 *   - /sysadmin/runtime/mcp       → useSysadminMcpSandboxes + useKillMcpSandbox
 *   - /sysadmin/runtime/queues    → useSysadminRuntimeQueues
 *   - /sysadmin/runtime/storage   → useSysadminRuntimeStorage
 *
 * Built on top of `createSysadminQuery` + `createSysadminMutation` from
 * use-sysadmin-query.ts. Schemas live HERE (vs duplicated across
 * routes) so the wire types stay in lockstep with the Fastify route
 * schemas — manual sync today, codegen target in v1.1.
 *
 * Refresh cadence: 10s default via createSysadminQuery so PR-D ticks
 * with the rest of the sysadmin operational surface.
 *
 * Note: the Health sub-tab is served from the older
 * `lib/use-sysadmin-runtime.ts` hook (W3-CELL-10.2#6) — see
 * `routes/sysadmin/runtime.health.tsx`. We do NOT re-export it from
 * here because the runtime-ops file would otherwise become "the place
 * for every runtime hook" and the Health hook predates Phase 5.
 */

import { z } from 'zod';
import {
  createSysadminQuery,
  createSysadminMutation,
} from './use-sysadmin-query';

// ════════════════════════════════════════════════════════════════════════
//  Hardware
// ════════════════════════════════════════════════════════════════════════

export const HostInfoSchema = z.object({
  available: z.boolean(),
  hostname: z.string(),
  os: z.string(),
  kernel: z.string(),
  cpuCores: z.number().int(),
  cpuModel: z.string().nullable(),
  memoryTotalMb: z.number().int().nullable(),
  memoryUsedMb: z.number().int().nullable(),
  memoryFreeMb: z.number().int().nullable(),
  diskTotalGb: z.number().int().nullable(),
  diskUsedGb: z.number().int().nullable(),
  diskFreeGb: z.number().int().nullable(),
  loadAvg: z.tuple([z.number(), z.number(), z.number()]),
  uptimeSeconds: z.number().int(),
  collectedAt: z.string(),
  notes: z.array(z.string()),
});
export type HostInfo = z.infer<typeof HostInfoSchema>;

export const GpuInfoRowSchema = z.object({
  index: z.number().int(),
  name: z.string(),
  vramTotalMb: z.number().int().nullable(),
  vramUsedMb: z.number().int().nullable(),
  utilizationPercent: z.number().int().nullable(),
  temperatureC: z.number().int().nullable(),
});
export type GpuInfoRow = z.infer<typeof GpuInfoRowSchema>;

export const GpuSummarySchema = z.object({
  available: z.boolean(),
  unavailableReason: z.string().nullable(),
  gpus: z.array(GpuInfoRowSchema),
  collectedAt: z.string(),
});
export type GpuSummary = z.infer<typeof GpuSummarySchema>;

export const HardwareResponseSchema = z.object({
  host: HostInfoSchema,
  gpu: GpuSummarySchema,
  collectedAt: z.string(),
});
export type HardwareResponse = z.infer<typeof HardwareResponseSchema>;

export const useSysadminRuntimeHardware = createSysadminQuery({
  key: ['sysadmin-runtime-hardware'],
  url: '/admin/platform/runtime/hardware',
  schema: HardwareResponseSchema,
});

// ════════════════════════════════════════════════════════════════════════
//  MCP Sandboxes (HIGH-RISK — kill switch)
// ════════════════════════════════════════════════════════════════════════

export const SandboxStatusSchema = z.enum([
  'starting',
  'running',
  'idle',
  'crashed',
  'killed',
  'stopped',
]);
export type SandboxStatus = z.infer<typeof SandboxStatusSchema>;

export const BreakerStateSchema = z.enum(['closed', 'open', 'half_open']);
export type BreakerState = z.infer<typeof BreakerStateSchema>;

export const SandboxRowSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  tenantSlug: z.string().nullable(),
  tenantName: z.string().nullable(),
  mcpInstallId: z.string().uuid(),
  serverSlug: z.string().nullable(),
  serverDisplayName: z.string().nullable(),
  containerId: z.string().nullable(),
  status: SandboxStatusSchema,
  circuitBreakerState: BreakerStateSchema,
  egressAttempts24h: z.number().int(),
  startedAt: z.string(),
  uptimeSeconds: z.number().int(),
  lastProbedAt: z.string().nullable(),
  /** True when governance_configs.itar_enabled = true for this tenant. */
  itarTenant: z.boolean(),
  cursor: z.string(),
});
export type SandboxRow = z.infer<typeof SandboxRowSchema>;

export const SandboxListResponseSchema = z.object({
  items: z.array(SandboxRowSchema),
  nextCursor: z.string().nullable(),
});
export type SandboxListResponse = z.infer<typeof SandboxListResponseSchema>;

export const useSysadminMcpSandboxes = createSysadminQuery({
  key: ['sysadmin-runtime-mcp-sandboxes'],
  url: '/admin/platform/runtime/mcp-sandboxes',
  schema: SandboxListResponseSchema,
});

// ─── Kill ────────────────────────────────────────────────────────────

export const KillSandboxInputSchema = z.object({
  id: z.string().uuid(),
  reason: z.string().min(10).max(2000),
  confirmed: z.literal(true),
  /** Required when the owning tenant has ITAR enabled. */
  ackItarReview: z.boolean().default(false),
});
export type KillSandboxInput = z.infer<typeof KillSandboxInputSchema>;

export const KillSandboxResponseSchema = z.object({
  success: z.literal(true),
  killedSandboxId: z.string().uuid(),
});
export type KillSandboxResponse = z.infer<typeof KillSandboxResponseSchema>;

export const useKillMcpSandbox = createSysadminMutation<
  KillSandboxInput,
  KillSandboxResponse
>({
  url: ({ id }) => `/admin/platform/runtime/mcp-sandboxes/${id}/kill`,
  method: 'POST',
  inputSchema: KillSandboxInputSchema,
  outputSchema: KillSandboxResponseSchema,
  invalidates: [['sysadmin-runtime-mcp-sandboxes']],
});

// ════════════════════════════════════════════════════════════════════════
//  Queues
// ════════════════════════════════════════════════════════════════════════

export const QueueRowSchema = z.object({
  name: z.string(),
  waiting: z.number().int(),
  active: z.number().int(),
  completed: z.number().int(),
  failed: z.number().int(),
  delayed: z.number().int(),
  paused: z.number().int(),
  probeError: z.string().nullable(),
});
export type QueueRow = z.infer<typeof QueueRowSchema>;

export const QueuesResponseSchema = z.object({
  queues: z.array(QueueRowSchema),
  collectedAt: z.string(),
});
export type QueuesResponse = z.infer<typeof QueuesResponseSchema>;

export const useSysadminRuntimeQueues = createSysadminQuery({
  key: ['sysadmin-runtime-queues'],
  url: '/admin/platform/runtime/queues',
  schema: QueuesResponseSchema,
  // Queues tick faster than the default 10s — operators want to spot
  // a stuck job within a few seconds of it happening.
  refreshMs: 5_000,
});

// ════════════════════════════════════════════════════════════════════════
//  Storage
// ════════════════════════════════════════════════════════════════════════

export const PostgresStorageSchema = z.object({
  available: z.boolean(),
  probeError: z.string().nullable(),
  databaseName: z.string().nullable(),
  databaseSizeBytes: z.number().nullable(),
  databaseSizeHuman: z.string().nullable(),
  walLsn: z.string().nullable(),
  replicationLagSeconds: z.number().nullable(),
});
export type PostgresStorage = z.infer<typeof PostgresStorageSchema>;

export const QdrantCollectionSchema = z.object({
  name: z.string(),
  pointsCount: z.number().int().nullable(),
  vectorsCount: z.number().int().nullable(),
  status: z.string().nullable(),
});
export type QdrantCollection = z.infer<typeof QdrantCollectionSchema>;

export const QdrantStorageSchema = z.object({
  available: z.boolean(),
  probeError: z.string().nullable(),
  collectionCount: z.number().int(),
  totalPoints: z.number().int(),
  collections: z.array(QdrantCollectionSchema),
});
export type QdrantStorage = z.infer<typeof QdrantStorageSchema>;

export const ValkeyStorageSchema = z.object({
  available: z.boolean(),
  probeError: z.string().nullable(),
  memoryUsedBytes: z.number().nullable(),
  memoryUsedHuman: z.string().nullable(),
  keyCount: z.number().int().nullable(),
});
export type ValkeyStorage = z.infer<typeof ValkeyStorageSchema>;

export const StorageResponseSchema = z.object({
  postgres: PostgresStorageSchema,
  qdrant: QdrantStorageSchema,
  valkey: ValkeyStorageSchema,
  collectedAt: z.string(),
});
export type StorageResponse = z.infer<typeof StorageResponseSchema>;

export const useSysadminRuntimeStorage = createSysadminQuery({
  key: ['sysadmin-runtime-storage'],
  url: '/admin/platform/runtime/storage',
  schema: StorageResponseSchema,
});
